import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { useSettings } from "../lib/useSettings";

const DEFAULT_HERO =
  "";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const settings = useSettings();
  const heroImg = settings?.hero_news || DEFAULT_HERO;

  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, []);

  const categories = ["all", ...new Set(items.map((i) => i.category))];
  const filtered =
    category === "all" ? items : items.filter((i) => i.category === category);

  return (
    <div data-testid="news-page">
      <SEO
        title="News & Insights"
        path="/news"
        description="Analisi, casi studio e aggiornamenti sull'ospitalità urbana, sui nostri progetti e sulla trasformazione di Milano."
        image={heroImg}
      />
      <PageHero
        eyebrow={settings?.hero_news_eyebrow || "Insights & News"}
        title={settings?.hero_news_title || "Osservatorio *urbano.*"}
        subtitle={
          settings?.hero_news_subtitle ||
          "Analisi, casi studio e aggiornamenti sull'ospitalità urbana, sui nostri progetti e sulla trasformazione di Milano."
        }
        image={heroImg}
      />

      <section className="relative py-16 md:py-24 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="flex gap-2 flex-wrap mb-10">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`news-filter-${c}`}
              onClick={() => setCategory(c)}
              className={`px-5 py-2.5 rounded-full text-sm border transition-colors ${
                category === c
                  ? "bg-white text-black border-white"
                  : "border-white/15 text-white/70 hover:text-white hover:border-white/30"
              }`}
            >
              {c === "all" ? "Tutti" : c}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((n, i) => (
            <motion.div
              key={n.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.06, duration: 0.6 }}
            >
              <Link
                to={`/news/${n.slug}`}
                data-testid={`news-item-${n.slug}`}
                className="group block rounded-2xl overflow-hidden border border-white/10 bg-[#0F0F14] hover:border-white/25 transition-colors h-full"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
                    style={{ backgroundImage: `url(${n.cover})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em]">
                    {n.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {new Date(n.date).toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}{" "}
                    · {n.read_time}
                  </div>
                  <h3 className="mt-3 font-display text-2xl tracking-tight leading-tight">
                    {n.title}
                  </h3>
                  <p className="mt-3 text-white/60 text-sm leading-relaxed line-clamp-3">
                    {n.excerpt}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {!filtered.length && (
          <div className="text-center py-20 text-white/40">
            Nessun articolo disponibile al momento.
          </div>
        )}
      </section>
    </div>
  );
}
